import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from '../services/api';

const WishlistContext = createContext(null);

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [wishlistIds, setWishlistIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshWishlist = useCallback(async () => {
    if (!isAuthenticated) {
      setWishlistIds([]);
      return;
    }

    try {
      setLoading(true);
      const response = await api.products.getWishlist();
      if (response.data?.success) {
        const ids = (response.data.wishlist || [])
          .map((item) => String(item.id || item._id || ''))
          .filter(Boolean);
        setWishlistIds(ids);
      }
    } catch (error) {
      console.error('Error loading wishlist:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authLoading) {
      refreshWishlist();
    }
  }, [authLoading, refreshWishlist]);

  const isInWishlist = useCallback(
    (productId) => {
      if (!productId) return false;
      return wishlistIds.includes(String(productId));
    },
    [wishlistIds]
  );

  const toggleWishlist = useCallback(
    async (productId) => {
      if (!productId) {
        return { success: false, error: 'Invalid product' };
      }

      if (!isAuthenticated) {
        navigate('/login');
        return { success: false, error: 'login_required' };
      }

      const id = String(productId);
      const alreadySaved = wishlistIds.includes(id);

      try {
        const response = alreadySaved
          ? await api.products.removeFromWishlist(id)
          : await api.products.addToWishlist(id);

        if (response.data?.success) {
          setWishlistIds((previous) =>
            alreadySaved ? previous.filter((item) => item !== id) : [...previous, id]
          );
          return { success: true, added: !alreadySaved };
        }

        return { success: false, error: response.data?.error || 'Could not update wishlist' };
      } catch (error) {
        return { success: false, error: api.utils.handleError(error) };
      }
    },
    [isAuthenticated, navigate, wishlistIds]
  );

  const value = {
    wishlistIds,
    loading,
    isInWishlist,
    toggleWishlist,
    refreshWishlist,
  };

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};
